import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Avatar } from "../../components/ui/avatar";
import { Clock, Calendar, Gem, Briefcase, Link, FileText, Check, ChevronRight, ArrowLeft, Bell } from "lucide-react";
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeToggle } from "../../components/theme-toggle";
import { ThemeProvider } from "../../components/theme-provider";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../../components/Sidebar";

type Bounty = {
  id: number;
  company: string;
  title: string;
  amount: string;
  dueTime: string;
  deliveryTime: string;
  type: string;
  comments: number;
  description: string;
  interested: number;
  avatars: string[];
  prizes: { label: string; amount: string }[];
  evolution: { title: string; content: string }[];
};

const BOUNTIES: Bounty[] = [    
  {
    id: 1,
    company: "Eigen Layer",
    title: "Design the new website for Eigen Layer",
    amount: "1200 USDC",
    dueTime: "1d 14h 13m",
    deliveryTime: "2 weeks",
    type: "Bounty",
    comments: 16,
    description: `Germany's Superteam is calling on all creative minds to design amazing merch! ...`,
    interested: 16,
    avatars: [
      "https://randomuser.me/api/portraits/men/32.jpg",
      "https://randomuser.me/api/portraits/women/44.jpg",
      "https://randomuser.me/api/portraits/men/45.jpg",
      "https://randomuser.me/api/portraits/women/46.jpg",
    ],
    prizes: [
      { label: "First Prize", amount: "800 USDC" },
      { label: "Second Prize", amount: "400 USDC" },
      { label: "Third Prize", amount: "200 USDC" },
    ],
    evolution: [
      { title: "Evolution Criteria", content: "Some criteria for evolution here." },
      { title: "Evolution Criteria", content: "Some more criteria for evolution here." },
    ],
  },
  // ...add the rest of your bounties here...
];

export default function BountyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const bounty = BOUNTIES.find(b => b.id === Number(id));

  const [openSections, setOpenSections] = useState<number[]>([]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!bounty) return <div>Bounty not found</div>;

  // Collapsible handler
  const toggleSection = (idx: number) => {
    setOpenSections(openSections.includes(idx)
      ? openSections.filter(i => i !== idx)
      : [...openSections, idx]);
  };

  // Dummy criteria for demo
  const criteria = [
    'Creativity and originality',
    'Alignment with Superteam Germany',
    'Quality of design and presentation',
  ];

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-[#FFF8F3]">
        {/* Back Button - now above the logo/title row */}
        <div className="px-20 pt-10 flex flex-col items-start">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6 bg-white text-black shadow-md rounded-xl px-6 py-3 flex items-center hover:bg-gray-100 transition border border-black"
            style={{marginBottom: '32px'}}
          >
            <ArrowLeft className="mr-2 h-5 w-5 text-black" />
            Back
          </Button>
          {/* Header */}
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-[#1602F2] rounded-full flex items-center justify-center">
                <span className="text-3xl font-bold text-white">{bounty.company[0]}</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-black">{bounty.title}</h1>
                  <Badge className="bg-green-600 text-white ml-2">Bounty</Badge>
                </div>
                <div className="text-[#888] text-sm font-medium mt-1">@{bounty.company.toLowerCase().replace(/ /g, '')}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#888] text-sm font-medium mr-2">{bounty.interested} Interested</span>
              <div className="flex -space-x-2">
                {bounty.avatars && bounty.avatars.map((src: string, i: number) => (
                  <Avatar key={i} className="w-8 h-8 border-2 border-white">
                    <img src={src} alt="Interested user" />
                  </Avatar>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Main Content */}
        <div className="flex gap-12 px-20 pt-8 pb-16">
          {/* Left/Main */}
          <div className="flex-1 max-w-3xl">
            {/* About Section */}
            <div className="border-b border-[#BDBDBD] pb-6 mb-6">
              <div className="uppercase text-xs text-[#888] font-bold mb-2 tracking-widest">About</div>
              <div className="text-black text-base leading-relaxed">
                {bounty.description.replace("...", "This is your opportunity to design exclusive t-shirts, stickers, and more that represent the essence of Superteam Germany and the Solana ecosystem.\n\nSome of the winning designs will get turned into physical merch!")}
              </div>
            </div>
            {/* Bounty Details Section */}
            <div className="border-b border-[#BDBDBD] pb-6 mb-6">
              <div className="uppercase text-xs text-[#888] font-bold mb-2 tracking-widest">Bounty Details</div>
              <div className="flex items-center gap-4 text-[#888] text-sm mb-2">
                <Clock className="w-4 h-4" /> Due in 12h
                <span className="mx-2">•</span>
                <span>Delivery time: {bounty.deliveryTime}</span>
                <span className="mx-2">•</span>
                <span>20 People applied ↗</span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg font-bold text-black">{bounty.title}</span>
                <span className="ml-2 px-3 py-1 rounded-full bg-[#F3E8FF] text-[#A259FF] text-xs font-bold">{bounty.amount}</span>
              </div>
              <div className="text-black text-base leading-relaxed">
                {bounty.description.replace("...", "This is your opportunity to design exclusive t-shirts, stickers, and more that represent the essence of Superteam Germany and the Solana ecosystem.\n\nSome of the winning designs will get turned into physical merch!")}
              </div>
            </div>
            {/* Evolution Criteria Sections */}
            {bounty.evolution.map((ev, idx) => (
              <div key={idx} className="border-b border-[#BDBDBD] pb-4 mb-4">
                <button
                  className="w-full flex items-center justify-between text-left py-2 px-0 focus:outline-none"
                  onClick={() => toggleSection(idx)}
                >
                  <span className="uppercase text-xs text-[#888] font-bold tracking-widest">{ev.title}</span>
                  <span className={`transition-transform ${openSections.includes(idx) ? 'rotate-90' : ''}`}>▶</span>
                </button>
                {openSections.includes(idx) && (
                  <div className="mt-2 text-black text-base leading-relaxed">{ev.content}</div>
                )}
              </div>
            ))}
          </div>
          {/* Sidebar Card */}
          <div className="w-[340px] flex-shrink-0">
            <div className="rounded-2xl border-2 border-black bg-[#FAF94F] p-6 shadow-lg flex flex-col gap-6">
              <div>
                <div className="uppercase text-xs text-[#888] font-bold mb-1 tracking-widest">Project Deadline</div>
                <div className="text-2xl font-mono text-black mb-2">
                  1d:14h:13m<span className="text-[#888] text-base ml-1">Remaining</span>
                </div>
              </div>
              <div>
                <div className="uppercase text-xs text-[#888] font-bold mb-1 tracking-widest">Total Prizes</div>
                <div className="text-2xl font-bold mb-2 flex items-center gap-2 text-[#1602F2]">
                  <Gem className="w-5 h-5 text-[#1602F2]" />
                  {bounty.amount}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between bg-[#FF5629] rounded-lg px-3 py-1">
                    <span className="text-white text-xs font-bold">First Prize</span>
                    <span className="text-white text-xs font-bold">800 USDC</span>
                  </div>
                  <div className="flex items-center justify-between bg-[#FFB029] rounded-lg px-3 py-1">
                    <span className="text-white text-xs font-bold">Second Prize</span>
                    <span className="text-white text-xs font-bold">400 USDC</span>
                  </div>
                  <div className="flex items-center justify-between bg-[#29B6FF] rounded-lg px-3 py-1">
                    <span className="text-white text-xs font-bold">Third Prize</span>
                    <span className="text-white text-xs font-bold">200 USDC</span>
                  </div>
                </div>
              </div>
              <Button className="w-full h-12 rounded-xl bg-black text-[#FAF94F] text-lg font-bold shadow-md hover:bg-[#222] transition">SUBMIT</Button>
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
} 